import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Checkbox, CircularProgress, FlatButton, MenuItem, RaisedButton, SelectField, TextField } from 'material-ui';
import moment from 'moment';
import { size, map, mapValues, get } from 'lodash';
import { getNumberOfDays, isHalfDay } from './holidays';
import pallet from '../../../constants/colors';
import useVacationStatusChecker from '../useVacationStatusChecker';

const errorStyle = {
  color: 'red',
  fontSize: '12px',
  margintop: 20,
};
const warningStyle = {
  color: 'orange',
  fontSize: '12px',
  margintop: 20,
};
const inputStyle = { width: 310, textAlign: 'left', marginTop: 10 };
const floatingStyle = { textAlign: 'left', width: 340 };

const TimeSpanEntryForm = ({
  defaultValues, selectedEntries, entryTypes, clearAndClose, span, holidayCheckInfo
}) => {
  const [form, setForm] = useState(defaultValues);

  const [errors, setErrors] = useState({});
  const [dateError, setDateError] = useState(false);
  const [moreThan24HoursError, setMoreThan24HoursError] = useState(false);
  const [loading, setLoading] = useState(false);

  const [warningText, setWarningText] = useState('');
  const [warningType, setWarningType] = useState('');
  const [showConfirmButton, setShowConfirmButton] = useState(false);
  const [showHalfDayWarning, setShowHalfDayWarning] = useState(false);

  const { validateHolidayStatus } = useVacationStatusChecker(holidayCheckInfo);

  const locale = localStorage.language === 'is' ? 'is' : 'en';

  const closeModal = () => {
    setShowConfirmButton(false);
    clearAndClose();
  };

  const reset = () => {
    setWarningText('');
    setWarningType('');
    setDateError(false);
    setMoreThan24HoursError(false);
    setShowConfirmButton(false);
  };

  const areErrors = () => {
    setErrors(mapValues(form, (v, key) => (key === 'hours' && v <= 0 ? true : !v)));
    if (!size(get(form, 'dateFrom')) || !size(get(form, 'dateTo')) || get(form, 'clockCardEntryTypeID') === null || get(form, 'hours') <= 0 || dateError) return true;

    if (parseFloat(get(form, 'hours')) > 24) {
      setMoreThan24HoursError(true);
      return true;
    }
    return false;
  };

  const checkIfDateError = (dateFrom, dateTo) => {
    const from = moment(moment(dateFrom).format('YYYY-MM-DD'));
    const to = moment(moment(dateTo).format('YYYY-MM-DD'));
    const isDateFromBeforeDateTo = to.isBefore(from);
    setDateError(isDateFromBeforeDateTo);
  };
  const isHalfDayInPeriod = (dayinout) => {
    if (dayinout == null) {
      setShowHalfDayWarning(false);
      return false;
    }
    const [DateFrom, DateTo] = dayinout;
    for (let current = DateFrom; current.isBefore(DateTo) || current.isSame(DateTo); current = current.add(1, 'day')) {
      if (isHalfDay(new Date(current))) {
        setShowHalfDayWarning(true);
        return true;
      }
    }
    setShowHalfDayWarning(false);
    return false;
  };
  const formChanged = (key, value) => {
    reset();
    const current = { ...form, [key]: value };
    setForm(current);
    if (key === 'dateTo' || key === 'dateFrom') {
      if (get(current, 'dateFrom') !== '' && key === 'dateTo') {
        checkIfDateError(get(current, 'dateFrom'), value, key);
      } else if (get(current, 'dateTo') !== '' && key === 'dateFrom') {
        checkIfDateError(value, get(current, 'dateTo'), key);
      }
      if (get(current, 'dateFrom') !== '' && get(current, 'dateTo') !== '') {
        isHalfDayInPeriod([moment(get(current, 'dateFrom')), moment(get(current, 'dateTo'))]);
      }
    }
    setErrors({ ...errors, [key]: key === 'hours' && value <= 0 ? true : !value });
  };

  const submitSpan = async () => {
    if (!areErrors()) {
      setLoading(true);

      const data = {
        ClockCardEntryTypeID: get(form, 'clockCardEntryTypeID'),
        ClockCardEntryComment: get(form, 'clockCardEntryComment'),
      };
      const timeIn = moment(get(form, 'dateFrom')).set({
        hour: 8, minute: 0, second: 0, millisecond: 0,
      });
      const timeOut = moment(timeIn);
      timeOut.add(get(form, 'hours'), 'hours');
      await span(
        data,
        timeIn,
        timeOut,
        moment(get(form, 'dateTo')).set({
          hour: timeOut.hours(), minute: timeOut.minute(), second: 0, millisecond: 0
        }),
        closeModal,
        get(form, 'registerWeekends'),
        get(form, 'registerRedDays'),
      );
      setLoading(false);
    }
  };

  const validateAndSubmit = async () => {
    if (areErrors()) {
      return;
    }
    setLoading(true);
    const typeId = get(form, 'clockCardEntryTypeID');
    const clockIn = moment(get(form, 'dateFrom'));
    const clockOut = moment(get(form, 'dateTo'));
    const hours = get(form, 'hours');
    const entryType = get(entryTypes, typeId);
    const isVacation = get(entryType, 'ClockCardEntryTypeRule') === 'Vacation';

    const ClockIn = clockIn.set({
      hour: 0, minute: 0, second: 0, millisecond: 0,
    });
    const ClockOut = clockOut;
    const numberOfDays = getNumberOfDays(ClockIn, ClockOut, get(form, 'registerRedDays'), get(form, 'registerWeekends'));
    const totalHoursRequest = numberOfDays * hours;
    if (isVacation) {
      const { text, type } = await validateHolidayStatus(parseFloat(totalHoursRequest));
      setWarningText(text);
      setWarningType(type);
      if (type === 'success') {
        await submitSpan();
      } else if (type === 'warning') {
        setShowConfirmButton(true);
      }
    } else {
      await submitSpan();
    }
    setLoading(false);
  };

  return (
    <div>
      <div style={{
        marginTop: 16,
        display: 'flex',
        marginBottom: 10,
      }}
      >
        <Checkbox
          label={<FormattedMessage id="time.RegisterWeekends" />}
          checked={get(form, 'registerWeekends')}
          onCheck={() => formChanged('registerWeekends', !get(form, 'registerWeekends'))}
          iconStyle={{ marginRight: 4 }}
        />
        <Checkbox
          label={<FormattedMessage id="time.registerRedDays" />}
          checked={get(form, 'registerRedDays')}
          onCheck={() => formChanged('registerRedDays', !get(form, 'registerRedDays'))}
          iconStyle={{ marginRight: 4 }}
        />
      </div>
      {showHalfDayWarning && (
        <div style={warningStyle}><FormattedMessage id="time.HalfDayWarning.Description" /></div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <TextField
          id="dateFrom"
          type="date"
          floatingLabelFixed
          floatingLabelText={<FormattedMessage id="time.DateIn" />}
          errorText={errors.dateFrom ? <FormattedMessage id="editPanel.required" /> : ''}
          value={get(form, 'dateFrom')}
          onChange={(event) => { formChanged('dateFrom', event.target.value); }}
        />

        <TextField
          id="dateTo"
          type="date"
          floatingLabelFixed
          floatingLabelText={<FormattedMessage id="time.DateOut" />}
          errorText={errors.dateTo ? <FormattedMessage id="editPanel.required" /> : ''}
          value={get(form, 'dateTo')}
          onChange={(event) => { formChanged('dateTo', event.target.value); }}
        />

        {dateError && <div style={errorStyle}><FormattedMessage id="time.error.DateOutBeforeDateIn" /></div>}

        <TextField
          id="hours"
          type="number"
          value={get(form, 'hours')}
          onChange={(_, val) => { formChanged('hours', val); }}
          hintStyle={inputStyle}
          style={inputStyle}
          inputStyle={inputStyle}
          floatingLabelStyle={floatingStyle}
          floatingLabelFixed
          floatingLabelText={<FormattedMessage id="time.totalHours.span" />}
        />
        {errors.hours && <div style={errorStyle}><FormattedMessage id="editPanel.required" /></div>}
        {moreThan24HoursError ? <div style={errorStyle}><FormattedMessage id="time.error.MoreThan24HoursTimeSpanError" /></div> : ''}
      </div>
      <SelectField
        fullWidth
        floatingLabelFixed
        floatingLabelText={<FormattedMessage id="time.Type" />}
        value={get(form, 'clockCardEntryTypeID')}
        onChange={(event, index, value) => { formChanged('clockCardEntryTypeID', value); }}
      >
        {map(entryTypes, ({ ClockCardEntryTypeID, ClockCardEntryTypeName, ClockCardEntryTypeName_EN: NameEnglish }) => (
          <MenuItem key={ClockCardEntryTypeID} value={ClockCardEntryTypeID} primaryText={locale === 'is' ? ClockCardEntryTypeName : NameEnglish} />
        ))}
      </SelectField>
      {errors.clockCardEntryTypeID && <div style={errorStyle}><FormattedMessage id="editPanel.required" /></div>}
      <span style={{ color: warningType === 'error' ? 'red' : pallet.warningColor }}>{warningText && warningText}</span>
      <TextField
        fullWidth
        floatingLabelFixed
        floatingLabelText={<FormattedMessage id="time.Comment" />}
        multiLine
        value={get(form, 'clockCardEntryComment')}
        onChange={(_, comment) => formChanged('clockCardEntryComment', comment)}
      />

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
        <FlatButton
          label={<FormattedMessage id="time.cancel" />}
          secondary
          onClick={() => closeModal()}
          disabled={loading}
        />
        {loading && <CircularProgress />}
        {!loading && (
          !showConfirmButton ? (
            <RaisedButton
              type="submit"
              label={size(selectedEntries) ? <FormattedMessage id="time.update" /> : <FormattedMessage id="time.create" />}
              primary
              onClick={validateAndSubmit}
              disabled={loading || dateError}
              loading={`${loading}`}
            />
          ) : (
            <RaisedButton
              type="submit"
              label={<FormattedMessage id="alert.ok" />}
              primary
              onClick={submitSpan}
              disabled={loading || dateError}
              loading={`${loading}`}
            />
          )
        )}
      </div>
    </div>
  );
};

TimeSpanEntryForm.propTypes = {
  clearAndClose: PropTypes.func.isRequired,
  defaultValues: PropTypes.shape({
    clockCardEntryComment: PropTypes.string,
    clockCardEntryTypeID: PropTypes.number,
    dateFrom: PropTypes.string,
    dateTo: PropTypes.string,
    hours: PropTypes.number,
    registerRedDays: PropTypes.bool,
    registerWeekends: PropTypes.bool,
  }).isRequired,
  entryTypes: PropTypes.shape({}).isRequired,
  selectedEntries: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  holidayCheckInfo: PropTypes.shape({
    detailId: PropTypes.number,
  }).isRequired,
  span: PropTypes.func.isRequired,
};

export default TimeSpanEntryForm;



// WEBPACK FOOTER //
// ./src/sites/time/components/TimeSpanEntryForm.js